import { RootState, RouteState, useCreateActionMutation, useGetCredentialsListQuery, useGetIntegrationEndpointsQuery, useGetMappersListQuery, useUpdateActionMutation, useEntityResourcePermissions } from '@circuitry-ai/doc-data';
import { checkType, CtyButton, CtyShowToast, CopyCodeField, optionalCodePayload } from '@circuitry-ai/doc-ui';
import { Autocomplete, AutocompleteItem, Divider, Input, Select, SelectItem, Textarea } from '@nextui-org/react';
import { useState, useEffect, useMemo, useRef, type MutableRefObject } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { type ActionMcpUpsertSnapshot } from '@circuitry-ai/doc-data';
import { useUXGenieTemplateSelection, UXGenieTemplateSelector } from './shared';

const NO_MAPPER_VALUE = 'no mapper';
const CACHE_OFF_VALUE = 'off';
const CACHE_TTL_OPTIONS = [
    { label: 'Off', value: CACHE_OFF_VALUE },
    { label: '5 minutes', value: '300' },
    { label: '15 minutes', value: '900' },
    { label: '1 hour', value: '3600' },
    { label: '24 hours', value: '86400' },
];

export interface ActionConfigurationProps {
    readonly onActionTitleChange?: (title: string) => void;
    readonly mcpUpsertSnapshotRef?: MutableRefObject<(() => ActionMcpUpsertSnapshot | null) | null>;
}

export function ActionConfiguration({ onActionTitleChange, mcpUpsertSnapshotRef }: Readonly<ActionConfigurationProps>) {
    const { t } = useTranslation();
    const location = useLocation();
    const navigate = useNavigate();
    const { mode } = useParams<{ mode: string; }>();
    const { action } = location?.state ?? {};
    const token = useSelector((state: RootState) => state.auth.token);
    const skipRef = useRef<boolean>(false);
    const isCreateMode = mode === RouteState.CREATE;
    const actionResourceId = action?.source_id || action?.aiworker_id;
    const { canUpdate } = useEntityResourcePermissions('action', isCreateMode ? undefined : actionResourceId);

    const [actionCode, setActionCode] = useState<string>('');
    const [actionTitle, setActionTitle] = useState<string>('');
    const [actionDescription, setActionDescription] = useState<string>('');
    const [actionType, setActionType] = useState<string>('');
    const [actionAPIMethod, setActionAPIMethod] = useState<string>('');
    const [actionRoute, setActionRoute] = useState<string>('');
    const [retry, setRetry] = useState<string>('1');
    const [actionTimeout, setActionTimeout] = useState<string>('');
    const [cacheTtl, setCacheTtl] = useState<string>(CACHE_OFF_VALUE);
    const [handleFunction, setHandleFunction] = useState<string>('');
    const [apiBodyType, setApiBodyType] = useState<string>('');
    const [authType, setAuthType] = useState<string>('');
    const [skip, setSkip] = useState<boolean>(true);

    const [authBodyType, setAuthBodyType] = useState<string>('');
    const [authRoute, setAuthRoute] = useState<string>('');
    const [authMethod, setAuthMethod] = useState<string>('');
    const [credentialId, setCredentialId] = useState<string>('');
    const [selectedMapperId, setSelectedMapperId] = useState<string>(NO_MAPPER_VALUE);

    // JSON fields & errors
    const [headers, setHeaders] = useState<string>('');
    const [headersError, setHeadersError] = useState<boolean>(false);
    const [apiBody, setApiBody] = useState<string>('');
    const [apiBodyError, setApiBodyError] = useState<boolean>(false);
    const [authHeaders, setAuthHeaders] = useState<string>('');
    const [authHeadersError, setAuthHeadersError] = useState<boolean>(false);
    const [authPayload, setAuthPayload] = useState<string>('');
    const [authPayloadError, setAuthPayloadError] = useState<boolean>(false);
    const [maskFields, setMaskFields] = useState<string>('');
    const [maskFieldsError, setMaskFieldsError] = useState<boolean>(false);

    const [headersFuncionList, setHeadersFuncionList] = useState<any[]>([]);

    // UXGenie Template selection - using shared hook
    const [initialTemplateId, setInitialTemplateId] = useState<string>('');
    const {
        selectedUXGenieWorkerId,
        setSelectedUXGenieWorkerId,
        uxgenieWorkers,
        selectedUXGenieTemplateId,
        setSelectedUXGenieTemplateId,
        uxgenieTemplates
    } = useUXGenieTemplateSelection(initialTemplateId);

    const [createAction, { isLoading: isActionSaving }] = useCreateActionMutation();
    const [updateAction, { isLoading: isActionUpdating }] = useUpdateActionMutation();
    const { data: headersList, isLoading, isFetching } = useGetIntegrationEndpointsQuery(undefined, { skip, refetchOnMountOrArgChange: true });
    const { data: credentialsList, isLoading: isCredentialsListLoading } = useGetCredentialsListQuery(undefined, {
        skip, 
        refetchOnMountOrArgChange: true 
    });
    const { data: mappersList = [], isLoading: isMappersLoading } = useGetMappersListQuery(undefined, {
        skip,
        refetchOnMountOrArgChange: true,
    });
    const credentialOptions = Array.isArray(credentialsList) ? credentialsList : [];
    const mapperOptions = useMemo(
        () =>
            mappersList.filter(
                (item) => item.aiworker_id && String(item.name ?? '').trim().length > 0
            ),
        [mappersList]
    );

    const actionTypeOptions = [
        { label: 'Internal API', value: 'internal_api' },
        { label: 'External API', value: 'external_api' },
    ];

    const actionAPIMethodOptions = [
        { label: 'GET', value: 'GET' },
        { label: 'POST', value: 'POST' },
        { label: 'PUT', value: 'PUT' },
        { label: 'DELETE', value: 'DELETE' },
        { label: 'PATCH', value: 'PATCH' },
    ];

    const authTypeOptions = [
        { label: 'No Auth', value: 'No Auth' },
        { label: 'OAuth', value: 'OAuth' }, 
        { label: 'Credentials', value: 'credentials'},
    ];

    const apiBodyTypeOptions = [
        { label: 'JSON', value: 'json' },
        { label: 'Form', value: 'form' },
        { label: 'Raw', value: 'raw' },
        { label: 'Multipart', value: 'multipart' },
    ];

    const retryOptions = ['1', '2', '3', '4', '5'];
    const cacheTtlSelectOptions = CACHE_TTL_OPTIONS.some((opt) => opt.value === cacheTtl)
        ? CACHE_TTL_OPTIONS
        : [...CACHE_TTL_OPTIONS, { label: `${cacheTtl} seconds`, value: cacheTtl }];

    useEffect(() => {
        if (token && !skipRef.current) {
            skipRef.current = true; //? CHECK IF THIS IS NEEDED
            setSkip(false);
        }
    }, [token]);

    useEffect(() => {
        if (headersList && !(isLoading || isFetching)) {
            const headersListData = (headersList ?? [])?.map((item: any) => {
                return {
                    label: item,
                    value: item
                };
            });
            setHeadersFuncionList(headersListData);
        }
    }, [headersList, isLoading, isFetching]);


    useEffect(() => {
        if (action) {

            setActionCode(action?.code ?? '');
            setActionTitle(action?.name);
            onActionTitleChange?.(action?.name ?? '');
            setActionDescription(action?.description);

            const attributes: Record<string, any> = {};
            action?.attributes.forEach((item: any) => {
                attributes[item?.attr_name] = item;
            });

            const apiRequest: Record<string, any> = attributes?.['api_spec']?.attr_json ?? {};
            const authDetails: Record<string, any> = apiRequest?.auth_details ?? {};
            const apiType = attributes?.['api_type']?.attr_value ?? '';
            const uxgenieTemplateId = attributes?.['uxgenie_template']?.attr_value ?? '';
            const credentialId = attributes?.['credential_id']?.attr_value ?? '';
            const mapperId = attributes?.['mapper']?.attr_value ?? '';

            setActionType(apiType);
            setInitialTemplateId(uxgenieTemplateId);
            setSelectedMapperId(mapperId || NO_MAPPER_VALUE);

            if (apiType === 'internal_api') {
                setHandleFunction(apiRequest?.name ?? '');
                setApiBody(JSON.parse(JSON.stringify(apiRequest?.sample_payload ?? '') ?? ''));
            } else {
                setActionAPIMethod(apiRequest?.method ?? '');
                setActionRoute(apiRequest?.route ?? '');
                setRetry(retryOptions.includes(String(apiRequest?.retry ?? '')) ? String(apiRequest.retry) : '1');
                setActionTimeout(apiRequest?.timeout != null && apiRequest.timeout !== '' ? String(apiRequest.timeout) : '');
                const savedCacheTtl = Number(apiRequest?.cache_ttl);
                setCacheTtl(
                    Number.isFinite(savedCacheTtl) && savedCacheTtl >= 1
                        ? String(Math.trunc(savedCacheTtl))
                        : CACHE_OFF_VALUE
                );
                setHeaders(JSON.parse(JSON.stringify(apiRequest?.headers ?? '') ?? '') ?? '');
                setAuthType(attributes?.['auth_type']?.attr_value ?? apiRequest?.auth_type ?? '');
                setApiBodyType(apiRequest?.api_body_type ?? '');
                setApiBody(JSON.parse(JSON.stringify(apiRequest?.payload ?? '') ?? ''));
                setAuthRoute(authDetails?.auth_route ?? '');
                setAuthMethod(authDetails?.auth_method ?? '');
                setAuthBodyType(authDetails?.auth_body_type ?? '');
                setAuthHeaders(JSON.parse(JSON.stringify(authDetails?.auth_headers ?? '') ?? '') ?? '');
                setAuthPayload(JSON.parse(JSON.stringify(authDetails?.auth_payload ?? '') ?? '') ?? '');
                setCredentialId(credentialId ?? '');
            }

            if (apiRequest?.mask_fields) {
                setMaskFields(JSON.stringify(apiRequest?.mask_fields ?? '') ?? '');
            }
        }
    }, [action, onActionTitleChange]);

    useEffect(() => {
        if (!mcpUpsertSnapshotRef) {
            return;
        }

        mcpUpsertSnapshotRef.current = () => {
            const sourceId = action?.source_id ?? action?.aiworker_id;
            if (!sourceId || !actionTitle.trim()) {
                return null;
            }

            return {
                sourceId,
                name: actionTitle.trim(),
                description: actionDescription,
                actionType,
                handleFunction,
                apiBody,
                actionRoute,
                actionAPIMethod,
            };
        };

        return () => {
            if (mcpUpsertSnapshotRef.current) {
                mcpUpsertSnapshotRef.current = null;
            }
        };
    }, [
        action,
        actionTitle,
        actionDescription,
        actionType,
        handleFunction,
        apiBody,
        actionRoute,
        actionAPIMethod,
        mcpUpsertSnapshotRef,
    ]);

    useEffect(() => {
        if (authType && authType !== 'credentials') {
            setCredentialId('');
        }
    }, [authType]);

    const validateJSON = (value: string): boolean => {
        try {
            JSON.parse(value);
            return true;
        } catch {
            return false;
        }
    };

    const handleSave = async () => {
        const isMaskFieldsValid = maskFields.trim().length > 0;
        const hdrErr = actionType === 'external_api' && !validateJSON(headers);
        const bodyErr = !validateJSON(apiBody);
        const maskFieldsErr = isMaskFieldsValid && !validateJSON(maskFields);
        const aHdrErr = authType === 'OAuth' && !validateJSON(authHeaders);
        const aPayErr = authType === 'OAuth' && authPayload.trim().length > 0 && !validateJSON(authPayload);

        console.log(hdrErr, bodyErr, aHdrErr, aPayErr, maskFieldsErr);

        setHeadersError(hdrErr);
        setApiBodyError(bodyErr);
        setAuthHeadersError(aHdrErr);
        setAuthPayloadError(aPayErr);
        setMaskFieldsError(maskFieldsErr);

        if (hdrErr || bodyErr || aHdrErr || aPayErr || maskFieldsErr) {
            CtyShowToast(checkType.error, t('Please fix JSON errors before saving'));
            return;
        }

        try {
            let payload = {};
            if (actionType === 'external_api') {
                const authDetails = {
                    auth_route: authRoute,
                    auth_headers: authHeaders,
                    auth_method: authMethod,
                    ...(authMethod && authMethod !== 'GET' && { auth_body_type: authBodyType }),
                    ...(authPayload.trim().length > 0 && { auth_payload: authPayload })
                };
                const parsedTimeout = Number(actionTimeout);
                const cacheTtlSeconds =
                    actionAPIMethod === 'GET' && cacheTtl !== CACHE_OFF_VALUE ? Number(cacheTtl) : 0;
                payload = {
                    route: actionRoute,
                    method: actionAPIMethod,
                    ...(actionAPIMethod && actionAPIMethod !== 'GET' && { api_body_type: apiBodyType }),
                    ...(authType === 'OAuth' ? { auth_details: authDetails } : {}),
                    headers: headers,
                    payload: apiBody,
                    retry: Number(retry) || 1,
                    ...(actionTimeout.trim() !== '' && Number.isFinite(parsedTimeout) && { timeout: parsedTimeout }),
                    ...(cacheTtlSeconds > 0 && { cache_ttl: cacheTtlSeconds }),
                    ...(isMaskFieldsValid && { mask_fields: JSON.parse(maskFields) }),
                };
            } else {
                payload = {
                    name: handleFunction,
                    sample_payload: apiBody,
                    ...(isMaskFieldsValid && { mask_fields: JSON.parse(maskFields) })
                };
            }

            const sourcesAttrs: any[] = [
                {
                    attr_name: 'api_type',
                    attr_value: actionType,
                },
                {
                    attr_name: 'auth_type',
                    attr_value: authType,
                },
                {
                    attr_name: 'api_spec',
                    attr_json: { ...payload }
                }
            ];

            if (authType === 'credentials' && credentialId) {
                sourcesAttrs.push({
                    attr_name: 'credential_id',
                    attr_value: credentialId,
                });
            }
            // Add UXGenie template if selected
            if (selectedUXGenieTemplateId) {
                sourcesAttrs.push({
                    attr_name: 'uxgenie_template',
                    attr_value: selectedUXGenieTemplateId,
                    attr_json: null
                });
            }
            sourcesAttrs.push({
                attr_name: 'mapper',
                attr_value: selectedMapperId || NO_MAPPER_VALUE,
            });


            const payLoad = {
                name: actionTitle,
                description: actionDescription,
                source_type: 'action',
                'sources_attrs': sourcesAttrs,
                ...(isCreateMode ? optionalCodePayload(actionCode) : {}),
            };

            const response = isCreateMode ? await createAction({ payLoad }).unwrap() : await updateAction({ payLoad, source_id: action?.source_id }).unwrap();
            const responseObject = { ...response?.data };
            responseObject['attributes'] = [...(responseObject?.['sources_attrs'] ?? {})];
            delete responseObject['sources_attrs'];

            CtyShowToast(checkType.success, t(response?.message));
            if (isCreateMode) navigate('/actions');
            else navigate(`${location.pathname}${location.search}`, { state: { action: responseObject } });
        } catch (error) {
            CtyShowToast(checkType.error, (error as Error)?.message ?? 'Operation failed');
        }
    };

    return (
        <div className="container-view flex-col gap-3 pb-3">
            <div className='container-scrollable w-full px-5'>
                <div className="md:w-[60%]">
                    <div className=" flex flex-col gap-3">
                        <CopyCodeField
                            label="Code"
                            value={actionCode}
                            isReadOnly={!isCreateMode}
                            isRequired={false}
                            onValueChange={isCreateMode ? setActionCode : undefined}
                        />
                        <div className="flex flex-col gap-1">
                            <label htmlFor="action_title">
                                <span className="text-[#414651] font-[Poppins] font-medium text-sm leading-[20px] tracking-normal">
                                    {t(`Action Title`)}
                                    <span className="text-danger">*</span>
                                </span>
                            </label>
                            <div className="flex-1">
                                <Input
                                    id="action_title"
                                    type="text"
                                    size="sm"
                                    aria-label="action_title"
                                    value={actionTitle}
                                    isRequired={true}
                                    onValueChange={(value) => {
                                        setActionTitle(value);
                                        onActionTitleChange?.(value);
                                    }}
                                />
                            </div>
                        </div>
                        <div className="flex flex-col gap-1">
                            <label htmlFor="action_description">
                                <span className="text-[#414651] font-[Poppins] font-medium text-sm leading-[20px] tracking-normal">
                                    {t(`Action description`)}
                                    <span className="text-danger">*</span>
                                </span>
                            </label>
                            <div className="flex-1">
                                <Textarea
                                    id="action_description"
                                    minRows={4}
                                    isRequired={true}
                                    maxRows={6}
                                    aria-label="action_description"
                                    value={actionDescription}
                                    onValueChange={setActionDescription}
                                />
                            </div>
                        </div>                       
                        <div className="flex flex-col gap-1">
                            <label htmlFor="action_type">
                                <span className="text-[#414651] font-[Poppins] font-medium text-sm leading-[20px] tracking-normal">
                                    {t(`Action Type`)}
                                    <span className="text-danger">*</span>
                                </span>
                            </label>
                            <Select
                                id="action_type"
                                placeholder={t('Select type')}
                                size="sm"
                                aria-label="action_type"
                                selectedKeys={new Set([actionType])}
                                disallowEmptySelection={true}
                                onSelectionChange={(keys) => setActionType(String([...keys][0]))}
                            >
                                {actionTypeOptions.map((opt) => (
                                    <SelectItem key={opt.value} value={opt.value}>
                                        {opt.label}
                                    </SelectItem>
                                ))}
                            </Select>
                        </div>
                        {actionType === 'external_api' && (
                            <>
                                <div className="flex flex-col gap-1">
                                    <label htmlFor="api_method">
                                        <span className="text-[#414651] font-[Poppins] font-medium text-sm leading-[20px] tracking-normal">
                                            {t(`API Method`)}
                                            <span className="text-danger">*</span>
                                        </span>
                                    </label>
                                    <Select
                                        id="api_method"
                                        placeholder={t('Select API Method')}
                                        size="sm"
                                        aria-label="api_method"
                                        selectedKeys={new Set([actionAPIMethod])}
                                        disallowEmptySelection={true}
                                        onSelectionChange={(keys) => setActionAPIMethod(String([...keys][0]))}
                                    >
                                        {actionAPIMethodOptions.map((opt) => (
                                            <SelectItem key={opt.value} value={opt.value}>
                                                {opt.label}
                                            </SelectItem>
                                        ))}
                                    </Select>
                                </div>
                                <div className="flex flex-col gap-1">
                                    <label htmlFor="api_route">
                                        <span className="text-[#414651] font-[Poppins] font-medium text-sm leading-[20px] tracking-normal">
                                            {t(`API Endpoint`)}
                                            <span className="text-danger">*</span>
                                        </span>
                                    </label>
                                    <Input
                                        id="api_route"
                                        type="text"
                                        size="sm"
                                        aria-label="api_route"
                                        value={actionRoute}
                                        isRequired={true}
                                        onValueChange={setActionRoute}
                                    />
                                </div>
                                <div className="flex flex-col gap-1">
                                    <label htmlFor="api_retry">
                                        <span className="text-[#414651] font-[Poppins] font-medium text-sm leading-[20px] tracking-normal">
                                            {t('Retry')}
                                        </span>
                                    </label>
                                    <Select
                                        id="api_retry"
                                        size="sm"
                                        aria-label="api_retry"
                                        selectedKeys={new Set([retry])}
                                        disallowEmptySelection={true}
                                        onSelectionChange={(keys) => setRetry(String([...keys][0]))}
                                    >
                                        {retryOptions.map((opt) => (
                                            <SelectItem key={opt} value={opt}>
                                                {opt}
                                            </SelectItem>
                                        ))}
                                    </Select>
                                </div>
                                <div className="flex flex-col gap-1">
                                    <label htmlFor="api_timeout">
                                        <span className="text-[#414651] font-[Poppins] font-medium text-sm leading-[20px] tracking-normal">
                                            {t('Timeout')}
                                        </span>
                                    </label>
                                    <Input
                                        id="api_timeout"
                                        type="number"
                                        size="sm"
                                        aria-label="api_timeout"
                                        value={actionTimeout}
                                        onValueChange={setActionTimeout}
                                    />
                                </div>
                                {actionAPIMethod === 'GET' && (
                                    <div className="flex flex-col gap-1">
                                        <label htmlFor="api_cache_ttl">
                                            <span className="text-[#414651] font-[Poppins] font-medium text-sm leading-[20px] tracking-normal">
                                                {t('Cache')}
                                            </span>
                                        </label>
                                        <Select
                                            id="api_cache_ttl"
                                            size="sm"
                                            aria-label="api_cache_ttl"
                                            selectedKeys={new Set([cacheTtl])}
                                            disallowEmptySelection={true}
                                            description={t('Reuse the response for identical requests for this long. Use only for data that rarely changes.')}
                                            onSelectionChange={(keys) => setCacheTtl(String([...keys][0]))}
                                        >
                                            {cacheTtlSelectOptions.map((opt) => (
                                                <SelectItem key={opt.value} value={opt.value}>
                                                    {t(opt.label)}
                                                </SelectItem>
                                            ))}
                                        </Select>
                                    </div>
                                )}
                                <div className="flex flex-col gap-1">
                                    <label htmlFor="api_header">
                                        <span className="text-[#414651] font-[Poppins] font-medium text-sm leading-[20px] tracking-normal">
                                            {t(`API Headers`)}
                                            <span className="text-danger">*</span>
                                        </span>
                                    </label>
                                    <Textarea
                                        id="api_header"
                                        minRows={4}
                                        isRequired={true}
                                        maxRows={6}
                                        placeholder='{ "Key": "Value" }'
                                        aria-label="api_header"
                                        isInvalid={headersError}
                                        errorMessage={headersError ? t('Invalid JSON') : ''}
                                        value={headers}
                                        onValueChange={setHeaders}
                                    />
                                </div>
                                {(actionAPIMethod && actionAPIMethod !== 'GET') && <div className="flex flex-col gap-1">
                                    <label htmlFor="api_body_type">
                                        <span className="text-[#414651] font-[Poppins] font-medium text-sm leading-[20px] tracking-normal">
                                            {t(`API Body Type`)}
                                            <span className="text-danger">*</span>
                                        </span>
                                    </label>
                                    <Select
                                        id="api_body_type"
                                        placeholder={t('Select API Body Type')}
                                        size="sm"
                                        aria-label="api_body_type"
                                        selectedKeys={new Set([apiBodyType])}
                                        disallowEmptySelection={true}
                                        onSelectionChange={(keys) => setApiBodyType(String([...keys][0]))}
                                    >
                                        {apiBodyTypeOptions.map((opt) => (
                                            <SelectItem key={opt.value} value={opt.value}>
                                                {opt.label}
                                            </SelectItem>
                                        ))}
                                    </Select>
                                </div>}
                                <div className="flex flex-col gap-1">
                                    <label htmlFor="api_body">
                                        <span className="text-[#414651] font-[Poppins] font-medium text-sm leading-[20px] tracking-normal">
                                            {t(`API Body`)}
                                            <span className="text-danger">*</span>
                                        </span>
                                    </label>
                                    <Textarea
                                        id="api_body"
                                        minRows={5}
                                        maxRows={7}
                                        aria-label="api_body"
                                        placeholder='{ "Key": "Value" }'
                                        value={apiBody}
                                        isInvalid={apiBodyError}
                                        errorMessage={apiBodyError ? t('Invalid JSON') : ''}
                                        onValueChange={setApiBody}
                                    />
                                </div>

                                <div className="flex flex-col gap-1">
                                    <label htmlFor="select_auth_type">
                                        <span className="text-[#414651] font-[Poppins] font-medium text-sm leading-[20px] tracking-normal">
                                            {t(`Auth type`)}
                                            <span className="text-danger">*</span>
                                        </span>
                                    </label>
                                    <Select
                                        id="select_auth_type"
                                        placeholder={t('Select type')}
                                        size="sm"
                                        aria-label="select_auth_type"
                                        selectedKeys={new Set([authType])}
                                        disallowEmptySelection={true}
                                        onSelectionChange={(keys) => setAuthType(String([...keys][0]))}
                                    >
                                        {authTypeOptions.map((opt) => (
                                            <SelectItem key={opt.value} value={opt.value}>
                                                {opt.label}
                                            </SelectItem>
                                        ))}
                                    </Select>
                                </div>

                                {authType === 'credentials' && (
                                    <div className="flex flex-col gap-1">
                                        <label htmlFor="select_credentials">
                                            <span className="text-[#414651] font-[Poppins] font-medium text-sm leading-[20px] tracking-normal">
                                                {t(`Credential List`)}
                                            </span>
                                        </label>
                                        <Autocomplete 
                                            id="select_credentials"
                                            placeholder={t('Select credentials')}
                                            size="sm"
                                            aria-label="select_credentials" selectedKey={credentialId || null}
                                            isLoading={isCredentialsListLoading}
                                            onSelectionChange={(key) => setCredentialId(key != null ? String(key) : '')}>
                                            {credentialOptions.map((credential) => (
                                            <AutocompleteItem key={credential.credential_id}>{credential.name}</AutocompleteItem>
                                            ))}
                                        </Autocomplete>
                                    </div>
                                )}

                                {authType === 'OAuth' &&
                                    <>
                                        <div className="flex flex-col gap-1">
                                            <label htmlFor="auth_method">
                                                <span className="text-[#414651] font-[Poppins] font-medium text-sm leading-[20px] tracking-normal">
                                                    {t(`Auth Method`)}
                                                    <span className="text-danger">*</span>
                                                </span>
                                            </label>
                                            <Select
                                                id="auth_method"
                                                placeholder={t('Select Auth Method')}
                                                size="sm"
                                                aria-label="auth_method"
                                                selectedKeys={new Set([authMethod])}
                                                disallowEmptySelection={true}
                                                onSelectionChange={(keys) => setAuthMethod(String([...keys][0]))}
                                            >
                                                {actionAPIMethodOptions.map((opt) => (
                                                    <SelectItem key={opt.value} value={opt.value}>
                                                        {opt.label}
                                                    </SelectItem>
                                                ))}
                                            </Select>
                                        </div>
                                        <div className="flex flex-col gap-1">
                                            <label htmlFor="auth_route">
                                                <span className="text-[#414651] font-[Poppins] font-medium text-sm leading-[20px] tracking-normal">
                                                    {t(`Auth Route`)}
                                                    <span className="text-danger">*</span>
                                                </span>
                                            </label>
                                            <Input
                                                id="auth_route"
                                                type="text"
                                                size="sm"
                                                aria-label="auth_route"
                                                value={authRoute}
                                                isRequired={true}
                                                onValueChange={setAuthRoute}
                                            />
                                        </div>
                                        <div className="flex flex-col gap-1">
                                            <label htmlFor="auth_header">
                                                <span className="text-[#414651] font-[Poppins] font-medium text-sm leading-[20px] tracking-normal">
                                                    {t(`Auth Headers`)}
                                                    <span className="text-danger">*</span>
                                                </span>
                                            </label>
                                            <Textarea
                                                id="auth_header"
                                                minRows={4}
                                                isRequired={true}
                                                maxRows={6}
                                                placeholder='{ "Key": "Value" }'
                                                aria-label="auth_header"
                                                value={authHeaders}
                                                isInvalid={authHeadersError}
                                                errorMessage={authHeadersError ? t('Invalid JSON') : ''}
                                                onValueChange={setAuthHeaders}
                                            />
                                        </div>
                                        {(authMethod && authMethod !== 'GET') && <div className="flex flex-col gap-1">
                                            <label htmlFor="select_auth_body_type">
                                                <span className="text-[#414651] font-[Poppins] font-medium text-sm leading-[20px] tracking-normal">
                                                    {t(`Auth Body type`)}
                                                    <span className="text-danger">*</span>
                                                </span>
                                            </label>
                                            <Select
                                                id="select_auth_body_type"
                                                placeholder={t('Select type')}
                                                size="sm"
                                                aria-label="select_auth_body_type"
                                                selectedKeys={new Set([authBodyType])}
                                                disallowEmptySelection={true}
                                                onSelectionChange={(keys) => setAuthBodyType(String([...keys][0]))}
                                            >
                                                {apiBodyTypeOptions.map((opt) => (
                                                    <SelectItem key={opt.value} value={opt.value}>
                                                        {opt.label}
                                                    </SelectItem>
                                                ))}
                                            </Select>
                                        </div>}
                                        <div className="flex flex-col gap-1">
                                            <label htmlFor="auth_payload">
                                                <span className="text-[#414651] font-[Poppins] font-medium text-sm leading-[20px] tracking-normal">
                                                    {t(`Auth Body`)}
                                                </span>
                                            </label>
                                            <Textarea
                                                id="auth_payload"
                                                minRows={4}
                                                isRequired={true}
                                                maxRows={6}
                                                placeholder='{ "Key": "Value" }'
                                                aria-label="auth_payload"
                                                value={authPayload}
                                                isInvalid={authPayloadError}
                                                errorMessage={authPayloadError ? t('Invalid JSON') : ''}
                                                onValueChange={setAuthPayload}
                                            />
                                        </div>
                                    </>
                                }

                            </>
                        )}
                        {actionType === 'internal_api' && (
                            <>
                                <div className="flex flex-col gap-1">
                                    <label htmlFor="select_header_type">
                                        <span className="text-[#414651] font-[Poppins] font-medium text-sm leading-[20px] tracking-normal">
                                            {t(`Handle Function`)}
                                            <span className="text-danger">*</span>
                                        </span>
                                    </label>
                                    <Select
                                        id="select_header_type"
                                        placeholder={t('Select header function')}
                                        size="sm"
                                        aria-label="select_header_type"
                                        selectedKeys={new Set([handleFunction])}
                                        disallowEmptySelection={true}
                                        onSelectionChange={(keys) => setHandleFunction(String([...keys][0]))}
                                    >
                                        {(headersFuncionList).map((opt) => (
                                            <SelectItem key={opt.value} value={opt.value}>
                                                {opt.label}
                                            </SelectItem>
                                        ))}
                                    </Select>
                                </div>
                                <div className="flex flex-col gap-1">
                                    <label htmlFor="api_body">
                                        <span className="text-[#414651] font-[Poppins] font-medium text-sm leading-[20px] tracking-normal">
                                            {t(`API Body`)}
                                            <span className="text-danger">*</span>
                                        </span>
                                    </label>
                                    <Textarea
                                        id="api_body"
                                        minRows={5}
                                        maxRows={7}
                                        aria-label="api_body"
                                        placeholder='{ "Key": "Value" }'
                                        value={apiBody}
                                        isInvalid={apiBodyError}
                                        errorMessage={apiBodyError ? t('Invalid JSON') : ''}
                                        onValueChange={setApiBody}
                                    />
                                </div>
                            </>
                        )}
                        <div className="flex flex-col gap-1">
                            <label htmlFor="action_mapper">
                                <span className="text-[#414651] font-[Poppins] font-medium text-sm leading-[20px] tracking-normal">
                                    {t('Mapper')}
                                </span>
                            </label>
                            <Select
                                id="action_mapper"
                                placeholder={t('Select mapper')}
                                size="sm"
                                aria-label="action_mapper"
                                selectedKeys={new Set([selectedMapperId || NO_MAPPER_VALUE])}
                                disallowEmptySelection
                                isLoading={isMappersLoading}
                                onSelectionChange={(keys) => {
                                    const next = String([...keys][0] ?? NO_MAPPER_VALUE);
                                    setSelectedMapperId(next);
                                }}
                            >
                                <SelectItem key={NO_MAPPER_VALUE} value={NO_MAPPER_VALUE}>
                                    {t('No mapper')}
                                </SelectItem>
                                {mapperOptions.map((item) => (
                                    <SelectItem key={item.aiworker_id} value={item.aiworker_id}>
                                        {item.name}
                                    </SelectItem>
                                ))}
                            </Select>
                        </div>

                        <div className="flex flex-col gap-1">
                            <label htmlFor="api_body">
                                <span className="text-[#414651] font-[Poppins] font-medium text-sm leading-[20px] tracking-normal">
                                    {t(`Mask Fields`)}
                                </span>
                            </label>
                            <Textarea
                                id="mask_fields"
                                minRows={5}
                                maxRows={7}
                                aria-label="mask_fields"
                                placeholder='{ "Key": "Value" }'
                                value={maskFields}
                                isInvalid={maskFieldsError}
                                errorMessage={maskFieldsError ? t('Invalid JSON') : ''}
                                onValueChange={setMaskFields}
                            />
                        </div>

                        {/* UXGenie Template Selection - Using shared component */}
                        <UXGenieTemplateSelector
                            selectedUXGenieWorkerId={selectedUXGenieWorkerId}
                            onWorkerChange={setSelectedUXGenieWorkerId}
                            uxgenieWorkers={uxgenieWorkers}
                            selectedUXGenieTemplateId={selectedUXGenieTemplateId}
                            onTemplateChange={setSelectedUXGenieTemplateId}
                            uxgenieTemplates={uxgenieTemplates}
                            layout="stacked"
                        />
                    </div>
                    <Divider className='my-3' />
                    <div className='flex justify-end'>
                        <CtyButton variant="solid" color="primary" size="md" radius="sm" onPress={handleSave} isLoading={isActionSaving || isActionUpdating} isDisabled={(!isCreateMode && !canUpdate) || isActionSaving || isActionUpdating}>
                            {isCreateMode ? 'Save' : 'Update'}
                        </CtyButton>
                    </div>
                </div>
            </div>
        </div>
    );
}